import type { ReactNode } from 'react';
import { Badge, type BadgeTone } from '@/components/ui';
import { PageContainer } from '@/components/ui/PageContainer';

/** A consistent Admin page header: title + subtitle on the left, actions on
 *  the right (e.g. a search box / refresh). */
export function AdminPageHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: ReactNode;
}) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold text-slate-100">{title}</h2>
        {subtitle ? <p className="mt-1 text-[13px] text-text-secondary">{subtitle}</p> : null}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}

/** Standard Admin page body padding. */
export function AdminPage({ children }: { children: ReactNode }) {
  return <PageContainer>{children}</PageContainer>;
}

/** A search input styled for the light admin (hairline border, indigo focus). */
export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder ?? 'Search…'}
      className="w-64 rounded-lg border border-ink-700 bg-ink-900 px-3 py-2 text-sm text-slate-100 placeholder:text-ink-500 transition-shadow focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
    />
  );
}

/** auth_users.status → a toned badge. */
export function AccountStatusBadge({ status }: { status: string }) {
  const tone: BadgeTone =
    status === 'active' ? 'ok' : status === 'blocked' ? 'danger' : status === 'pending' ? 'warn' : 'neutral';
  return (
    <Badge tone={tone} dot>
      {status || 'unknown'}
    </Badge>
  );
}

/** Document review status (14/15/16/17) → a toned badge. */
export function DocStatusBadge({ status, name }: { status: number; name: string }) {
  const tone: BadgeTone =
    status === 16 ? 'ok' : status === 17 ? 'danger' : status === 15 ? 'warn' : 'neutral';
  return <Badge tone={tone}>{name}</Badge>;
}

// Pure formatters (personName / km / dateTime) live in '@/lib/adminFormat' so
// this component file exports components only (react-refresh fast-refresh).
